import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { AxiosError } from "axios"
import axios from "src/utils/axios"
import {
  ConvertSchemaWithAIParameterType,
  CreateDatabaseParameterType,
  GetDatabaseParameterType,
  SearchDatabasesParameterType,
  UpdateDatabaseParameterType
} from "../applicationServices/database"
import {
  DatabaseResponseType,
  DatabasesResponseType
} from "../responseBuilders/database"

export class DatabaseApi {
  public static async getDatabase(
    params: Omit<GetDatabaseParameterType, "userId">
  ) {
    const res = await axios.get<DatabaseResponseType>(
      `/api/v1.0/databases/${params.databaseId}`
    )
    return res.data
  }

  public static async searchDatabases(
    param: Omit<SearchDatabasesParameterType, "userId">
  ) {
    const res = await axios.get<DatabasesResponseType>(
      "/api/v1.0/databases/search",
      {
        params: param
      }
    )
    return res
  }

  public static async createDatabase(
    param: Omit<CreateDatabaseParameterType, "userId">
  ) {
    const res = await axios.post<DatabaseResponseType>(
      "/api/v1.0/databases",
      param
    )
    return res.data
  }

  public static async updateDatabase(
    params: Omit<UpdateDatabaseParameterType, "userId">
  ) {
    const res = await axios.patch<DatabaseResponseType>(
      `/api/v1.0/databases/${params.databaseId}`,
      params
    )
    return res.data
  }

  public static async deleteDatabase({ databaseId }: { databaseId: string }) {
    const res = await axios.delete<DatabaseResponseType>(
      `/api/v1.0/databases/${databaseId}`
    )
    return res.data
  }

  public static async convertSchemaWithAI(
    params: Omit<ConvertSchemaWithAIParameterType, "userId">
  ) {
    const res = await axios.post<DatabaseResponseType>(
      `/api/v1.0/databases/${params.databaseId}/schemas`,
      params
    )
    return res.data
  }
}

export const useCreateDatabase = () => {
  const queryClient = useQueryClient()
  return useMutation(
    (param: Omit<CreateDatabaseParameterType, "userId">) =>
      DatabaseApi.createDatabase(param),
    {
      onSuccess: () => queryClient.invalidateQueries(["databases", "search"]),
      onError: (error: AxiosError) => {
        const serverErrors = error.response?.data?.errors
        if (serverErrors && serverErrors.length > 0) {
          const errorCode = serverErrors[0].code
          throw new Error(errorCode)
        }
      }
    }
  )
}

export const useGetDatabase = (
  params: Omit<GetDatabaseParameterType, "userId">
) =>
  useQuery({
    queryKey: ["databases", "get", params.databaseId],
    queryFn: () => DatabaseApi.getDatabase(params)
  })

export const useUpdateDatabase = (params: {
  invalidateDatabaseSearch?: boolean
  invalidateDatabaseGet?: boolean
}) => {
  const { invalidateDatabaseSearch = false, invalidateDatabaseGet = true } =
    params
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (param: Omit<UpdateDatabaseParameterType, "userId">) =>
      DatabaseApi.updateDatabase(param),
    onSuccess: (_, variables) => {
      if (invalidateDatabaseSearch) {
        queryClient.invalidateQueries(["databases", "search"])
      }
      if (invalidateDatabaseGet) {
        queryClient.invalidateQueries([
          "databases",
          "get",
          variables.databaseId
        ])
      }
    }
  })
}

export const useSearchDatabases = (param: {
  teamId: string
  status?: number
  enabled?: boolean
  staleTime?: number
  cacheTime?: number
}) => {
  const {
    teamId,
    enabled = true,
    staleTime = Infinity,
    cacheTime = Infinity
  } = param
  return useQuery({
    queryKey: ["databases", "search", param],
    queryFn: () =>
      DatabaseApi.searchDatabases({
        query: {
          teamId: param.teamId
        }
      }),
    staleTime,
    cacheTime,
    enabled
  })
}

export const useDeleteDatabase = (params: {
  invalidateDatabaseSearch?: boolean
}) => {
  const { invalidateDatabaseSearch = false } = params
  const queryClient = useQueryClient()
  return useMutation(
    (databaseId: string) => DatabaseApi.deleteDatabase({ databaseId }),
    {
      onSuccess: async () => {
        if (invalidateDatabaseSearch) {
          await queryClient.invalidateQueries(["databases", "search"])
        }
      }
    }
  )
}

export const useConvertSchemaWithAI = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: { databaseId: string; schema: string }) => {
      const response = await DatabaseApi.convertSchemaWithAI({
        databaseId: params.databaseId,
        schema: params.schema
      })
      return response
    },
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries(["databases", "get"])
    }
  })
}
